import React, { type ReactNode } from "react";

interface Props {
  label: string;
  /**
   * Rendered in place of the children once they throw. As a function it gets
   * `retry`, which mounts the children again (a query then reads again).
   */
  fallback: ReactNode | ((retry: () => void) => ReactNode);
  children: ReactNode;
}

interface State {
  failed: boolean;
}

// For a query a page can do without: when the children throw (a Convex query
// that errors throws into the nearest boundary), the fallback takes their
// place quietly and the rest of the page goes on. A section whose failure the
// viewer must see uses SectionBoundary, with its message and Retry.
export class FallbackBoundary extends React.Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error): void {
    console.error(`FallbackBoundary[${this.props.label}]`, error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const { fallback } = this.props;
    return typeof fallback === "function"
      ? fallback(() => this.setState({ failed: false }))
      : fallback;
  }
}
